import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';

export const EmailVerification: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email...');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Get token from URL
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (!token) {
          setStatus('error');
          setMessage('Invalid verification link. Please check your email for the correct link.');
          return;
        }

        // Send verification request - Corrected to GET with token in path
        const response = await fetch(`/api/auth/verify-email/${token}`, {
          method: 'GET'
          // No headers or body needed for GET
        });

        const data = await response.json();

        if (response.ok) {
          setStatus('success');
          setMessage('Your email has been verified successfully! You can now log in to your account.');
        } else {
          setStatus('error');
          setMessage(data.error || 'Failed to verify email. The link may have expired or is invalid.');
        }
      } catch (error) {
        setStatus('error');
        setMessage('An error occurred during verification. Please try again later.');
      }
    };

    verifyEmail();
  }, [location]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md mx-4 border-2 border-indigo-400 dark:border-indigo-500">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Email Verification</CardTitle>
          <CardDescription className="text-center">
            {status === 'loading' ? 'Please wait while we verify your email address.' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className={`mb-4 ${
            status === 'error' 
              ? 'bg-red-100 dark:bg-red-900 text-red-900 dark:text-red-100' 
              : status === 'success' 
                ? 'bg-green-100 dark:bg-green-900 text-green-900 dark:text-green-100' 
                : 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100'
          }`}>
            <AlertDescription>{message}</AlertDescription>
          </Alert>

          {status === 'loading' && (
            <div className="flex justify-center my-6">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-500 dark:border-indigo-400 border-t-transparent"></div>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          {status !== 'loading' && (
            <Button
              onClick={() => navigate('/login')}
              className="w-full bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-800"
            >
              {status === 'success' ? 'Go to Login' : 'Back to Login'}
            </Button>
          )}
        </CardFooter>
      </Card>
    </div>
  );
}; 